// RPC layer + event bus over the existing WebSocket. Lets the dashboard
// invoke any Electron IPC handler that was registered via bothHandle() in
// electron-app/main.js, and subscribe to push events.
//
//   await rpc.call("get-accounts");
//   await rpc.call("switch-account", "riley_…");
//   rpc.onEvent("login-states-batch", (states) => ...);

import type { ControlClient, ControlMessage } from "./control";

const DEFAULT_TIMEOUT_MS = 8000;

export class Rpc {
  private control: ControlClient;
  private pending = new Map<string, { resolve: (v: unknown) => void; reject: (e: Error) => void; timer: number }>();
  private eventHandlers = new Map<string, Set<(data: unknown) => void>>();

  constructor(control: ControlClient) {
    this.control = control;
    control.on("rpc_response", (msg) => this.handleResponse(msg));
    control.on("event", (msg) => this.handleEvent(msg));
  }

  async call<T = unknown>(channel: string, ...args: unknown[]): Promise<T> {
    if (!this.control.isConnected()) {
      throw new Error("RPC unavailable — control plane disconnected");
    }
    const id = randomId();
    return new Promise<T>((resolve, reject) => {
      const timer = window.setTimeout(() => {
        this.pending.delete(id);
        reject(new Error(`RPC ${channel} timed out`));
      }, DEFAULT_TIMEOUT_MS);
      this.pending.set(id, {
        resolve: resolve as (v: unknown) => void,
        reject,
        timer,
      });
      this.control.send({
        type: "rpc_request",
        payload: { id, channel, args },
      });
    });
  }

  onEvent<T = unknown>(name: string, fn: (data: T) => void): () => void {
    let set = this.eventHandlers.get(name);
    if (!set) {
      set = new Set();
      this.eventHandlers.set(name, set);
    }
    const wrapped = fn as (data: unknown) => void;
    set.add(wrapped);
    return () => set!.delete(wrapped);
  }

  private handleResponse(msg: ControlMessage): void {
    const p = (msg.payload || {}) as { id?: string; ok?: boolean; data?: unknown; error?: string };
    if (!p.id) return;
    const entry = this.pending.get(p.id);
    if (!entry) return;
    this.pending.delete(p.id);
    window.clearTimeout(entry.timer);
    if (p.ok) entry.resolve(p.data);
    else entry.reject(new Error(p.error ?? "RPC failed"));
  }

  private handleEvent(msg: ControlMessage): void {
    const p = (msg.payload || {}) as { name?: string; data?: unknown };
    if (!p.name) return;
    const set = this.eventHandlers.get(p.name);
    if (!set) return;
    for (const fn of set) {
      try { fn(p.data); } catch (e) { console.error("[rpc] event handler error", e); }
    }
  }
}

function randomId(): string {
  return Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);
}
